import InboxActionTypes from "./inbox.types";
import NAME_DATA from "../../data";
import {
  loadPersistedFlags,
  persistFlags,
  mergeWithPersistedFlags,
  shuffleArray,
} from "./inbox.utils";

const persisted = loadPersistedFlags();

const INITIAL_STATE = {
  emails: shuffleArray(mergeWithPersistedFlags(NAME_DATA, persisted)),
  searchQuery: "",
  deletedEmails: [], // undo stack: { id, email } entries
  archivedEmails: [], // undo stack for archives
};

const inboxReducer = (state = INITIAL_STATE, action) => {
  let updated;

  switch (action.type) {
    case InboxActionTypes.TOGGLE_STAR:
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, starred: !e.starred } : e
      );
      persistFlags(updated);
      return { ...state, emails: updated };

    case InboxActionTypes.TOGGLE_READ:
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, read: !e.read } : e
      );
      persistFlags(updated);
      return { ...state, emails: updated };

    case InboxActionTypes.MARK_AS_READ:
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, read: true } : e
      );
      persistFlags(updated);
      return { ...state, emails: updated };

    case InboxActionTypes.DELETE_EMAIL: {
      const email = state.emails.find((e) => e.id === action.payload);
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, deleted: true, selected: false } : e
      );
      persistFlags(updated);
      return {
        ...state,
        emails: updated,
        deletedEmails: email
          ? [{ id: email.id, email: { ...email } }, ...state.deletedEmails]
          : state.deletedEmails,
      };
    }

    case InboxActionTypes.UNDO_DELETE: {
      if (state.deletedEmails.length === 0) return state;
      const [last, ...rest] = state.deletedEmails;
      updated = state.emails.map((e) =>
        e.id === last.id ? { ...last.email, deleted: false, selected: false } : e
      );
      persistFlags(updated);
      return { ...state, emails: updated, deletedEmails: rest };
    }

    case InboxActionTypes.TOGGLE_SELECT:
      return {
        ...state,
        emails: state.emails.map((e) =>
          e.id === action.payload ? { ...e, selected: !e.selected } : e
        ),
      };

    case InboxActionTypes.SELECT_ALL:
      return {
        ...state,
        emails: state.emails.map((e) =>
          !e.deleted ? { ...e, selected: true } : e
        ),
      };

    case InboxActionTypes.DESELECT_ALL:
      return {
        ...state,
        emails: state.emails.map((e) => ({ ...e, selected: false })),
      };

    case InboxActionTypes.BULK_DELETE: {
      const toDelete = state.emails.filter((e) => e.selected && !e.deleted);
      updated = state.emails.map((e) =>
        e.selected ? { ...e, deleted: true, selected: false } : e
      );
      persistFlags(updated);
      return {
        ...state,
        emails: updated,
        deletedEmails: [
          ...toDelete.map((e) => ({ id: e.id, email: { ...e } })),
          ...state.deletedEmails,
        ],
      };
    }

    case InboxActionTypes.RESTORE_EMAIL:
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, deleted: false } : e
      );
      persistFlags(updated);
      return {
        ...state,
        emails: updated,
        deletedEmails: state.deletedEmails.filter((d) => d.id !== action.payload),
      };

    case InboxActionTypes.ARCHIVE_EMAIL: {
      const archEmail = state.emails.find((e) => e.id === action.payload);
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, archived: true, selected: false } : e
      );
      persistFlags(updated);
      return {
        ...state,
        emails: updated,
        archivedEmails: archEmail
          ? [{ id: archEmail.id, email: { ...archEmail } }, ...state.archivedEmails]
          : state.archivedEmails,
      };
    }

    case InboxActionTypes.UNDO_ARCHIVE: {
      if (state.archivedEmails.length === 0) return state;
      const [lastArch, ...restArch] = state.archivedEmails;
      updated = state.emails.map((e) =>
        e.id === lastArch.id ? { ...lastArch.email, archived: false, selected: false } : e
      );
      persistFlags(updated);
      return { ...state, emails: updated, archivedEmails: restArch };
    }

    case InboxActionTypes.RESTORE_ARCHIVE:
      updated = state.emails.map((e) =>
        e.id === action.payload ? { ...e, archived: false } : e
      );
      persistFlags(updated);
      return {
        ...state,
        emails: updated,
        archivedEmails: state.archivedEmails.filter((a) => a.id !== action.payload),
      };

    case InboxActionTypes.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };

    default:
      return state;
  }
};

export default inboxReducer;
